/* The plain words the Home Wi-Fi screen says. The box judges each device
 * (cloud-anchor/pkg/homewifi) and answers codes and figures; every sentence a
 * person reads is written here, from what the box answered, and nothing here
 * claims what the box did not measure. */

export const EXPERIENCE = {
  poor: "Having a bad time",
  fair: "Struggling a little",
  good: "Doing fine",
  unknown: "Can't tell",
  idle: "Not in use",
};

/* One meaning per colour: red a bad time, amber struggling, green fine, grey
   nothing to judge. */
export const TONE = { poor: "bad", fair: "warn", good: "ok", unknown: "off", idle: "off" };

const APPS = {
  APP_NETFLIX: "Netflix", APP_YOUTUBE: "YouTube", APP_TWITCH: "Twitch", APP_AMAZON_VIDEO: "Prime Video",
  APP_DISNEYPLUS: "Disney+", APP_HULU: "Hulu", APP_HBO: "Max", APP_APPLE_TV: "Apple TV", APP_TIKTOK: "TikTok",
  APP_FACEBOOK_REEL: "Facebook", APP_SPOTIFY: "Spotify", APP_ZOOM: "Zoom", APP_GOOGLE_MEET: "Google Meet",
  APP_MSTEAMS_CALL: "Teams", APP_WHATSAPP_CALL: "WhatsApp", APP_SIGNAL_VOIP: "Signal", APP_TELEGRAM_VOIP: "Telegram",
  APP_FACEBOOK_VOIP: "Messenger", APP_VIBER_VOIP: "Viber", APP_WEBEX: "Webex", APP_SNAPCHAT_CALL: "Snapchat",
  APP_GOOGLE_CALL: "Google", APP_DISCORD: "Discord", APP_STEAM: "Steam", APP_DROPBOX: "Dropbox",
  APP_GOOGLE_DRIVE: "Google Drive", APP_APPLE_ICLOUD: "iCloud", APP_MICROSOFT: "Microsoft", APP_APPLE: "Apple",
};

export function appName(app) {
  if (!app) return "";
  if (APPS[app]) return APPS[app];
  return app.replace(/^APP_/, "").toLowerCase().replace(/(^|_)(\w)/g, (_, sep, c) => (sep ? " " : "") + c.toUpperCase());
}

/* What a device is doing, as a person would say it. */
export function doing(activity) {
  const app = appName(activity?.app);
  switch (activity?.kind) {
    case "call": return app ? `On a ${app} call` : "On a call";
    case "live": return "Playing online or on a call";
    case "video": return app ? `Watching ${app}` : "Watching video";
    case "music": return app ? `Listening on ${app}` : "Listening to music";
    case "browsing": return "Browsing";
    case "download": return app ? `Downloading from ${app}` : "Downloading";
    case "background": return "Updating in the background";
    default: return "Connected";
  }
}

/* What the activity needs, in the words the person would check against. */
function needs(activity, need) {
  if (activity?.kind === "video") return "an HD picture needs 5 and 4K needs 15";
  if (activity?.kind === "call") return `a video call needs about ${need}`;
  return `this needs about ${need}`;
}

const pct = (x) => `${Math.round(x * 100)}%`;
const mbit = (x) => (x >= 10 ? Math.round(x) : Math.round(x * 10) / 10);

/* The sentence a cause says, given the rest of the home to name another
   device by. */
export function causeSentence(c, d, others = []) {
  const v = c.values || {};
  switch (c.code) {
    case "weak_signal":
      return `Its Wi-Fi signal is weak (${v.signal_dbm} dBm): it is far from the router, or walls are in the way.`;
    case "retries":
      return `The router has to send ${pct(v.retries)} of its Wi-Fi frames to it again.`;
    case "crowded_channel":
      return `Neighbours' Wi-Fi keeps its channel busy ${pct(v.busy_others)} of the time.`;
    case "airtime_hog": {
      const o = others.find((x) => x.mac === c.device);
      return `${o ? displayName(o) : "Another device"} is taking ${pct(v.airtime)} of the Wi-Fi's time${o?.activity?.kind === "download" ? " with a download" : ""}.`;
    }
    case "slow_link":
      return `Its Wi-Fi can carry about ${mbit(v.link_mbps)} Mbit/s right now; ${needs(d?.activity, v.need_mbps)}.`;
    case "saturated":
      return `It is using nearly all its Wi-Fi can carry: ${mbit(v.down_mbps)} of about ${mbit(v.link_mbps)} Mbit/s.`;
    case "slow_band":
      return "It is on the slower 2.4 GHz band, and it can use the box's 5 GHz network.";
    case "slow_radio":
      return `Its Wi-Fi can never carry more than about ${mbit(v.max_mbps)} Mbit/s here: an older or single-antenna radio.`;
    case "slow_inside": {
      const live = d?.activity?.kind === "call" || d?.activity?.kind === "live";
      const outside = v.outside_p95_ms != null ? `; outside the home it takes ${Math.round(v.outside_p95_ms)}` : "";
      const want = live ? ", where a call or a game wants under 30" : "";
      return `Inside the home its traffic takes up to ${Math.round(v.p95_ms)} ms to get through${v.loss > 0 ? ` and ${pct(v.loss)} is lost` : ""}${outside}${want}.`;
    }
    case "slow_outside":
      return `Outside the home its traffic takes up to ${Math.round(v.p95_ms)} ms${v.inside_p95_ms != null ? `, and inside only ${Math.round(v.inside_p95_ms)}` : ""}: the slow part is the internet line or the service.`;
    case "line_queue":
      return `The way out of the home takes ${Math.round(v.queue_ms)} ms longer than when it is quiet, so everything this device does waits.`;
    case "resent":
      return `${pct(v.resent)} of what reaches it has to be sent again: it is lost between the router and the device.`;
    case "internet":
      return v.working ? "The internet line is not working as it should." : "The internet line is down.";
    case "no_view":
      return "It is on an access point this box does not run, or on a cable, so the box cannot see its Wi-Fi.";
    case "stuck_far":
      return `It holds on to a far access point (${v.signal_dbm} dBm) while its own scan hears a nearer one of the same network at ${v.better_dbm} dBm.`;
    case "neighbours":
      return `${v.networks} other ${v.networks === 1 ? "network shares" : "networks share"} its channel, the strongest at ${v.strongest_dbm} dBm.`;
    default:
      return c.code;
  }
}

/* The one reason a row leads with: the worst cause, nearest the device. A
   note leads only a row with something to explain. */
export function leadCause(d, explain = true) {
  const order = { poor: 0, fair: 1, note: 2 };
  const causes = (d?.verdict?.causes || []).filter((c) => explain || c.severity !== "note" || c.code === "slow_band");
  causes.sort((a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3));
  return causes[0] || null;
}

/* Where the trouble is, as the path drawing marks it. */
export const WHERE = {
  wifi: "The trouble is its Wi-Fi.",
  home: "The trouble is inside the home, between the router and the device.",
  internet: "The trouble is the internet line, not the Wi-Fi.",
};

/* Whether a device can use 5 GHz: its own scan hears 5 GHz, or its radio says so. */
const hears5 = (d) => (d?.client?.heard || []).some((h) => (h.freq_mhz || 0) > 5000) || (d?.station?.caps?.bands || []).includes("5 GHz");

export function remedySentence(r, d, others = [], radios = []) {
  switch (r.code) {
    case "steer_band": {
      const to = radios.find((x) => x.name === r.target);
      return `The box can ask it to move to its ${to?.band || "faster"} network, which carries more. The device decides whether to go.`;
    }
    case "make_room": {
      const o = others.find((x) => x.mac === r.target);
      return `The box can slow ${o ? displayName(o) : "the other device"}'s download while this one needs the room, and gives the speed back when it is done.`;
    }
    case "limit_other": {
      const o = others.find((x) => x.mac === r.target);
      return `Slow ${o ? displayName(o) : "the other device"} down while this one needs the room: give it a speed limit on Device Speed Limits.`;
    }
    case "move_closer":
      return "Move it nearer the router, or add an access point near where it sits.";
    case "quiet_channel": {
      const on = radios.find((x) => x.name === r.target);
      return `Put the box's ${on ? `${on.band} ` : ""}Wi-Fi on a quieter channel.`;
    }
    case "client_app":
      return "If it is a phone or a computer, run the Weavian app on it so it reports its own Wi-Fi to the box.";
    case "rejoin":
      return `Turn its Wi-Fi off and on again: ${rejoinWhy(d, radios)}.`;
    case "check_lines":
      return "The internet line itself is slow or full. If it lasts, call your internet provider.";
    default:
      return r.code;
  }
}

/* What turning a device's Wi-Fi off and on would do for it. */
function rejoinWhy(d, radios = []) {
  const band = radios.find((x) => x.name === d?.station?.radio)?.band || d?.client?.link?.band;
  if ((d?.verdict?.causes || []).some((c) => c.code === "stuck_far")) return "it should then pick the nearer access point";
  if (band === "2.4 GHz" && hears5(d)) return "it should rejoin on 5 GHz, which is far quieter here";
  return "it should then pick the faster band";
}

export function actionLabel(r, radios = []) {
  if (r.code === "steer_band") {
    const to = radios.find((x) => x.name === r.target);
    return `Move it to ${to?.band || "the faster band"}`;
  }
  if (r.code === "make_room") return "Make room for it";
  return r.code;
}

export function confirmRoom(d, r, others = []) {
  const o = others.find((x) => x.mac === r.target);
  const slowed = o ? displayName(o) : "the other device";
  return {
    title: `Make room for ${displayName(d)}?`,
    message: `The box slows ${slowed}'s download while ${displayName(d)} needs the room. After a minute it checks whether ` +
      `${displayName(d)} got better: if not, it gives the speed back at once. It also gives it back once ${displayName(d)} is done.`,
    confirmLabel: "Make room",
    variant: "primary",
  };
}

export const GIVE_BACK = "Give the speed back";

/* The make room setting, as a person chooses it. */
export const ROOM_MODES = [
  { value: "ask", label: "Only when I ask" },
  { value: "auto", label: "By itself" },
  { value: "off", label: "Off" },
];

export const ROOM_EXPLAINED =
  "When a device on a call, in a game or watching something is struggling while another runs a big download, " +
  "the box can slow the download until the room is no longer needed. Each time, it checks the struggling device " +
  "got better, and gives the speed back if it did not.";

export function confirmMove(d, r, radios = []) {
  const to = radios.find((x) => x.name === r.target);
  return {
    title: `Move ${displayName(d)} to ${to?.band || "the other band"}?`,
    message: `The box asks ${displayName(d)} to move to its ${to?.band || "other"} network${to?.ssid ? ` "${to.ssid}"` : ""}. ` +
      "It stays connected while it moves, and it may decide to stay where it is.",
    confirmLabel: "Ask it to move",
    variant: "primary",
  };
}

/* The answer the screen leads with. */
export function headline(view) {
  const devices = view?.devices || [];
  const s = view?.summary || {};
  if (devices.length === 0) return { tone: "off", text: "No devices on the home network yet." };
  if (!s.active) return { tone: "off", text: `Nothing is in use right now. ${devices.length} ${devices.length === 1 ? "device is" : "devices are"} connected.` };
  // Worst first, as the list below it.
  const trouble = devices.filter((d) => d.verdict?.experience === "poor" || d.verdict?.experience === "fair")
    .sort((a, b) => (a.verdict.experience === "poor" ? 0 : 1) - (b.verdict.experience === "poor" ? 0 : 1));
  if (trouble.length === 0) {
    return {
      tone: "ok",
      text: s.unknown
        ? `Every device in use is doing fine, as far as the box can tell: it cannot judge ${s.unknown} of the ${s.active} from here.`
        : "Every device in use is doing fine.",
    };
  }
  const names = trouble.slice(0, 3).map(displayName);
  const more = trouble.length > 3 ? ` and ${trouble.length - 3} more` : "";
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  return {
    tone: s.poor ? "bad" : "warn",
    text: `${trouble.length} of ${s.active} ${s.active === 1 ? "device" : "devices"} in use ${trouble.length === 1 ? "is" : "are"} having trouble: ${list}${more}.`,
  };
}

export function problemSentence(p) {
  switch (p.code) {
    case "no_radio":
      return "This box serves no Wi-Fi of its own, so it judges each device by its traffic, what it does and how quickly it gets through, rather than by its radio.";
    case "no_control":
      return `The box cannot ask devices on ${p.target} to move: the access point software does not answer for it.`;
    case "no_echoes":
      return "The box cannot echo its devices, so a game or a call over a link the box cannot see is not judged.";
    case "no_timing":
      return "The box cannot time its devices' traffic, so a device on an access point it does not run is judged by less.";
    case "radio_error":
      return "The box could not read its own radios this time; it tries again on its own.";
    case "no_counters":
      return "The box does not account its devices' traffic, so it cannot say what each is doing.";
    case "no_state":
      return "The box could not read whether it makes room by itself, so it makes room only when someone asks.";
    case "ap_unreadable":
      return `The box could not read the access point${p.target ? ` at ${p.target}` : ""}: ${p.detail || "it did not answer"}.`;
    default:
      return p.code;
  }
}

/* One radio of the home's access points, as a sentence: the box's own, or
   one the box reads over another access point's interface. */
export function radioSentence(r) {
  const net = `${r.ssid ? `"${r.ssid}"` : "A network"} on ${r.band}, channel ${r.channel}${r.width_mhz ? ` (${r.width_mhz} MHz wide)` : ""}`;
  const on = r.device ? `${r.device}: ${net}` : net;
  const who = `${r.stations} ${r.stations === 1 ? "device" : "devices"}`;
  const busy = r.busy_others != null ? ` Neighbours keep it busy ${pct(r.busy_others)} of the time${r.busy_estimated ? " (estimated)" : ""}.` : "";
  return `${on}: ${who}.${busy}`;
}

const BAND = (s) => s?.band || "its band";

function roomSentence(f, time, name) {
  const slowed = f.slowed_name || (f.slowed ? `…${f.slowed.slice(-5)}` : "another device");
  const who = f.auto ? "The box" : "Asked, the box";
  const made = `${time}: ${who} slowed ${slowed} to ${f.down_mbit} Mbit/s to make room for ${name}`;
  const went = f.before && f.after ? ` (${EXPERIENCE[f.before.experience]?.toLowerCase() || f.before.experience} before, ${EXPERIENCE[f.after.experience]?.toLowerCase() || f.after.experience} after)` : "";
  switch (f.state) {
    case "trying":
      return `${made}; checking whether it helps.`;
    case "kept":
      return `${made}: it helped${went}, so it holds while ${name} needs it.`;
    case "undone":
      return `${made}: it did not help${went}, so the box gave the speed back.`;
    case "released": {
      const why = {
        done: `${name} no longer needs the room`,
        quiet: `${slowed} finished its download`,
        expired: "it had held for half an hour",
        restart: "the box restarted",
        person: "someone asked",
      }[f.why] || "it was no longer needed";
      return `${made}. Speed given back: ${why}.`;
    }
    default:
      return `${made}.`;
  }
}

export function fixSentence(f, time) {
  const name = f.name || (f.device ? `…${f.device.slice(-5)}` : "the device");
  if (f.remedy === "make_room") return roomSentence(f, time, name);
  switch (f.state) {
    case "asked":
      return `${time}: asked ${name} to move to ${f.to}; waiting for it to answer.`;
    case "moved": {
      const gain = f.before?.link_mbps != null && f.after?.link_mbps != null
        ? `: its Wi-Fi went from about ${mbit(f.before.link_mbps)} to ${mbit(f.after.link_mbps)} Mbit/s`
        : "";
      return `${time}: ${name} moved from ${BAND(f.before)} to ${BAND(f.after)}${gain}.`;
    }
    case "stayed":
      return `${time}: asked ${name} to move; it stayed on ${BAND(f.before)}.`;
    case "left":
      return `${time}: asked ${name} to move; it left the network instead.`;
    default:
      return `${time}: ${f.state}`;
  }
}

/* The measurements behind a verdict, each a line under "How the box knows". */
export function measurements(d, radio) {
  const out = [];
  const st = d.station;
  if (st) {
    const who = radio?.device ? `the ${radio.device}` : "the box";
    if (radio) out.push(`On ${radio.device ? `the ${radio.device}'s` : "the box's"} ${radio.band} network "${radio.ssid || radio.name}", channel ${radio.channel}.`);
    if (st.signal_dbm) out.push(`Signal ${who} hears from it: ${st.signal_dbm} dBm.`);
    if (st.down?.mbps) out.push(`${who.charAt(0).toUpperCase()}${who.slice(1)} to it: ${Math.round(st.down.mbps)} Mbit/s negotiated${st.down.generation ? ` (${st.down.generation}, ${st.down.width_mhz} MHz, ${st.down.streams} ${st.down.streams === 1 ? "stream" : "streams"})` : ""}.`);
    if (d.verdict?.link_mbps != null) out.push(`Of that, about ${mbit(d.verdict.link_mbps)} Mbit/s reaches it as data right now.`);
    if (st.retries >= 0) out.push(`Frames sent again: ${pct(st.retries)} of ${st.frames}.`);
    if (st.airtime > 0) out.push(`Share of the Wi-Fi's time it took: ${pct(st.airtime)}.`);
    if (st.caps?.bands?.length) out.push(`It can use ${st.caps.bands.join(" and ")}${st.caps.steerable ? ", and it takes requests to move" : ""}.`);
  }
  const a = d.activity || {};
  if (a.kind && a.kind !== "idle") out.push(`It is pulling ${mbit(a.down_mbps)} Mbit/s from the internet and sending ${mbit(a.up_mbps)}.`);
  const c = d.client?.link;
  if (c) {
    const on = [c.band, c.channel ? `channel ${c.channel}` : ""].filter(Boolean).join(", ");
    out.push(`Its own app reports: ${c.signal_dbm} dBm${on ? ` on ${on}` : ""}${c.rx?.mbps ? `, receiving at ${Math.round(c.rx.mbps)} Mbit/s` : ""}${c.standard ? ` (${c.standard})` : ""}.`);
  }
  if (d.client?.heard?.length) out.push(`Its own scan hears ${d.client.heard.length} ${d.client.heard.length === 1 ? "network" : "networks"} around it.`);
  if (d.client?.probe?.answered) out.push(`Its own trips to the box: ${Math.round(d.client.probe.p50_ms)} ms typical, ${Math.round(d.client.probe.p95_ms)} ms at worst.`);
  const t = d.timing;
  if (t?.inside) out.push(`Its traffic inside the home, timed by the box: ${Math.round(t.inside.p50_ms)} ms typical, ${Math.round(t.inside.p95_ms)} ms at worst (${t.inside.samples} timings).`);
  if (t?.outside) out.push(`Outside the home: ${Math.round(t.outside.p50_ms)} ms typical, ${Math.round(t.outside.p95_ms)} ms at worst (${t.outside.samples} timings).`);
  if (t?.resent >= 0) out.push(`Sent to it again after the box had passed it on: ${pct(t.resent)} of ${t.segments}.`);
  const l = d.latency;
  if (l?.answered) out.push(`The box's echoes to it: ${Math.round(l.p50_ms)} ms typical, ${Math.round(l.p95_ms)} ms at worst, ${l.sent - l.answered} of ${l.sent} lost.`);
  else if (l?.sent) out.push(`It did not answer the box's echoes (${l.sent} sent), which some devices never do.`);
  return out;
}

/* The coverage map from a walk through the home. */
export const MAP_NONE =
  "Walk the home with the Weavian app to map its Wi-Fi: start beside the router, walk through every room at an " +
  "easy pace, and mark where the television and the other devices that stay put sit.";

/* Signal in the colours the map uses: one meaning each. */
export function signalTone(dbm) {
  if (dbm == null) return "none";
  if (dbm >= -60) return "strong";
  if (dbm >= -67) return "good";
  if (dbm >= -70) return "fair";
  return "weak";
}

export const SIGNAL_WORDS = {
  strong: "Strong: anything works",
  good: "Good: 4K video and calls hold",
  fair: "Fair: HD video plays, calls may break up",
  weak: "Weak: video may stall, calls may drop",
};

const pctWord = (x) => `${Math.round(x * 100)}%`;

export function mapHeadline(a) {
  if (!a) return "";
  if (a.problems?.includes("no_network")) return "The walk heard no network the phone was on, so there is nothing to map.";
  if (!a.aps?.length) return "The walk was too short to place the access point. Walk a little longer, through more rooms.";
  const weak = a.weak_share > 0 ? `${pctWord(a.weak_share)} of the places walked are weak.` : "Every place walked gets a good signal.";
  const units = new Set(a.aps.map((x) => x.unit)).size;
  const placed = units === 1 ? "The walk placed the access point" : `The walk placed ${units} access points`;
  return `${placed} and mapped the signal around it. ${weak}`;
}

export function markSentence(m) {
  if (m.dbm == null) return `${m.label}: the walk did not reach it.`;
  const tone = signalTone(m.dbm);
  const how = { strong: "a strong signal", good: "a good signal", fair: "a fair signal", weak: "a weak signal" }[tone];
  const ext = m.with_extender != null && m.with_extender > m.dbm + 2 ? `; with the extender, about ${Math.round(m.with_extender)} dBm` : "";
  return `${m.label}: ${how} (${Math.round(m.dbm)} dBm${m.measured ? "" : ", expected"})${ext}.`;
}

export function extenderSentence(e) {
  if (!e) return "";
  const near = e.near ? ` about ${e.near_m} m from ${e.near}` : "";
  const after = e.weak_after > 0
    ? `It would leave ${pctWord(e.weak_after)} of the places walked weak, where ${pctWord(e.weak_before)} are today.`
    : `It would leave no place walked weak, where ${pctWord(e.weak_before)} are today.`;
  return `Put an extender at the star${near}, where the router still reaches it at ${Math.round(e.backhaul_dbm)} dBm. ${after}`;
}

/* The fleet's view: homes in counts, no one named. */
const REASON = {
  weak_signal: "a weak signal", slow_link: "a link too slow for what it does", crowded_channel: "a crowded channel",
  airtime_hog: "another device taking the Wi-Fi", saturated: "a full link", retries: "frames sent again",
  slow_inside: "a slow path inside the home", resent: "data lost inside the home", slow_outside: "a slow path outside the home",
  internet: "the internet line", line_queue: "a slow way out of the home", stuck_far: "devices holding on to a far access point", neighbours: "neighbours' networks",
};
export const reasonWord = (code) => REASON[code] || code;

const WHERE_SHORT = { wifi: "the Wi-Fi", home: "inside the home", internet: "the internet line" };

const top = (m) => Object.entries(m || {}).sort((a, b) => b[1] - a[1])[0]?.[0];

export function fleetHeadline(customers) {
  const homes = (customers || []).flatMap((c) => c.homes || []).filter((h) => !h.old);
  if (homes.length === 0) return { tone: "off", text: "No home has reported its Wi-Fi yet." };
  const struggling = homes.filter((h) => h.struggling > 0);
  if (struggling.length === 0) {
    return { tone: "ok", text: `No device is struggling in any of the ${homes.length} ${homes.length === 1 ? "home" : "homes"} right now.` };
  }
  const where = {};
  for (const h of struggling) {
    const w = top(h.report.where);
    if (w) where[w] = (where[w] || 0) + 1;
  }
  const parts = Object.entries(where).sort((a, b) => b[1] - a[1]).map(([w, n]) => `in ${n} the trouble is ${WHERE_SHORT[w] || w}`);
  return {
    tone: struggling.some((h) => h.report.summary.poor > 0) ? "bad" : "warn",
    text: `${struggling.length} of ${homes.length} homes have a device struggling right now${parts.length ? `: ${parts.join(", ")}` : ""}.`,
  };
}

export function homeSentence(h) {
  const s = h.report.summary;
  if (!s.active) return `Nothing in use right now; ${s.idle} ${s.idle === 1 ? "device" : "devices"} connected.`;
  if (!h.struggling) return `All ${s.active} ${s.active === 1 ? "device" : "devices"} in use are doing fine.`;
  // The reason says where it is; the place alone stands in when no reason led.
  const why = top(h.report.causes);
  const where = top(h.report.where);
  const said = why ? reasonWord(why) : WHERE_SHORT[where] || "";
  return `${h.struggling} of ${s.active} devices in use ${h.struggling === 1 ? "is" : "are"} struggling${said ? `: ${said}` : ""}.`;
}

export function homeExtras(h) {
  const out = [];
  if (h.report.rooms) out.push(`The box is making room for ${h.report.rooms === 1 ? "a device" : `${h.report.rooms} devices`}.`);
  const sv = h.report.survey;
  if (sv) out.push(sv.extender ? `Its last walk found ${Math.round(sv.weak_share * 100)}% of the home weak and an extender spot.` : "Its last walk found no weak spot.");
  if (h.report.reporting) out.push(`${h.report.reporting} ${h.report.reporting === 1 ? "device reports" : "devices report"} through the Weavian app.`);
  return out;
}

/* Where the devices that report stand, from what each hears. */
export const PLACES_TITLE = "Where your devices are";
export const PLACES_HOW =
  "Placed from what each device hears: how loud the router is to it, and which devices hear the neighbours' Wi-Fi alike. " +
  "Room-level, within a few metres. A walk with the phone fixes the scale and turns this into a map of the home.";

/** Where one device stands, in a sentence: how far from what sends the Wi-Fi. */
export function placeSentence(p, source = "the router") {
  const far = `About ${Math.max(1, Math.round(p.from_router_m))} m from ${source}, judged by its signal`;
  if (p.by === "walk" && p.radius_m <= 4) return `${far}; on the map where the walk found the same networks.`;
  return `${far}.`;
}


/* The screen as redesigned on 4 Nov 2026: who each device is, how it is
   connected, the route its traffic takes, and what would make the Wi-Fi
   better. */

const PLATFORMS = [
  [/^android/i, "Android"], [/^ios|^iphone|^ipad/i, "iOS"], [/^windows/i, "Windows"],
  [/^mac|^darwin/i, "macOS"], [/^linux/i, "Linux"],
];

/** The platform a device's Weavian app reports, as people say it. */
export function platformWord(p) {
  const hit = PLATFORMS.find(([re]) => re.test(p || ""));
  return hit ? hit[1] : "";
}

/** Which picture a device gets: a computer, a phone or tablet, or a device. */
export function kindOf(d) {
  const p = platformWord(d?.client?.platform);
  if (p === "Android" || p === "iOS") return "mobile";
  if (p) return "computer";
  return "device";
}

/** A device's name: its own, else what its app says it is; never an address. */
export function displayName(d) {
  if (d?.name) return d.name;
  const p = platformWord(d?.client?.platform);
  if (p) return kindOf(d) === "mobile" ? `${p} device with no name` : `${p} computer with no name`;
  if (d?.client?.model) return `${d.client.model} with no name`;
  return "A device with no name";
}

/* Naming a device. */
/* A sound a device plays so a person can find it (homewifi ring.go). */
export const RING_HINT = "Plays a loud sound on it for half a minute, so you can find it by ear.";
export function ringSentence(r) {
  switch (r.state) {
    case "asked":
      return "Asked it to play a sound. It hears within half a minute.";
    case "ringing":
      return "Playing a sound now. Follow it to the device.";
    case "rang":
      return `It played a sound for ${Math.round(r.for_s || 30)} seconds.`;
    default:
      return "It played nothing: its Weavian app has not been in touch since you asked.";
  }
}

export const NAME_HINT = "A name, like Kitchen tablet";
export const ownName = (d) => `You named it; on the network it calls itself ${d.hostname}.`;
export const ownNameBack = (d) => (d.hostname ? `Use ${d.hostname}` : "Remove the name");

/** The line under a device's name: what it is, or where it is on the network. */
export function deviceSub(d) {
  const p = platformWord(d?.client?.platform);
  const model = d?.client?.model;
  if (p) return [p, model].filter(Boolean).join(" · ");
  return d?.ip ? `At ${d.ip}, no Weavian app` : "No Weavian app";
}

const SIGNAL_WORD = { strong: "strong signal", good: "good signal", fair: "fair signal", weak: "weak signal" };

/** How a device is connected: its band and how strong its signal is. */
export function connectionWords(d, radio) {
  const link = d?.client?.link;
  const band = radio?.band || link?.band;
  const dbm = link?.signal_dbm ?? d?.station?.signal_dbm;
  if (!band && dbm == null) return d?.station ? "Wi-Fi" : "Wi-Fi or a cable";
  const tone = signalTone(dbm);
  return [band || "Wi-Fi", SIGNAL_WORD[tone]].filter(Boolean).join(" · ");
}

/** The figure under the headline: devices, the Wi-Fi's air, the internet. */
export function heroFacts(view) {
  const devices = view?.devices || [];
  const s = view?.summary || {};
  const out = [`${s.active || 0} of ${devices.length} ${devices.length === 1 ? "device" : "devices"} in use`];
  const o = view?.outside;
  if (o?.lines) {
    const all = o.lines === 1 ? "the internet line" : o.lines === 2 ? "both internet lines" : `all ${o.lines} internet lines`;
    const full = (o.queue_ms || 0) >= 50 ? `${o.lines === 1 ? "the internet line is" : `${all} are`} up, but full` : "";
    out.push(full || (o.working === o.lines ? `${all} working` : `${o.working} of ${o.lines} internet lines working`));
  }
  return out.join(" · ");
}

/* A cause in a few words, for the headline. */
const SHORT = {
  weak_signal: "weak signal", crowded_channel: "crowded Wi-Fi", neighbours: "crowded Wi-Fi", slow_link: "slow Wi-Fi",
  airtime_hog: "another device is taking the Wi-Fi", saturated: "its Wi-Fi is full", retries: "Wi-Fi errors",
  slow_band: "on the slow 2.4 GHz band", slow_radio: "an old Wi-Fi radio", stuck_far: "holding on to a far access point",
  slow_inside: "slow inside the home", resent: "data lost inside the home", slow_outside: "slow past the home",
  line_queue: "the internet line is full", internet: "internet line trouble", no_view: "the box cannot see its Wi-Fi",
};

/** The devices in trouble, worst first, each with its reason in a few words. */
export function troubleFacts(trouble) {
  return trouble.map((d) => {
    const c = leadCause(d, true);
    return c && SHORT[c.code] ? `${displayName(d)}: ${SHORT[c.code]}` : displayName(d);
  }).join(" · ");
}

/** The one fix the headline leads with on a bad day: the worst device's first. */
export function troubleTodo(d, others = [], radios = [], source = "the router") {
  const r = (d?.verdict?.remedies || []).find((x) => !x.action) || (d?.verdict?.remedies || [])[0];
  if (!r) return null;
  const name = displayName(d);
  switch (r.code) {
    case "rejoin":
      return `Turn ${name}'s Wi-Fi off and on: ${rejoinWhy(d, radios)}.`;
    case "move_closer":
      return `Move ${name} nearer ${source}, or add an extender near where it sits.`;
    case "check_lines":
      return `The internet line is slowing ${name}. If it lasts, call your internet provider.`;
    case "steer_band":
    case "make_room":
      return `${name}: ${actionLabel(r, radios)}, from its row.`;
    default:
      return `${name}: ${remedySentence(r, d, others, radios)}`;
  }
}

/** A row's first sentence: where the trouble is, then why. */
export function troubleLine(where, why) {
  const at = WHERE[where];
  if (!at || !why) return why || at || "";
  return `${at.replace(/\.$/, "")}: ${why.charAt(0).toLowerCase()}${why.slice(1)}`;
}

/* One hop of a device's route in words: what it is, its word, and its one
   telling figure (the same as the router's own page, pkg/homepage). */
const ms = (x) => (x < 1 ? "under 1 ms" : `${Math.round(x)} ms`);

export function hopWords(h, cloud) {
  const v = h.values || {};
  const st = h.status || "unknown";
  const word = { good: "Good", fair: "Fair", poor: "Poor", unknown: "Not read" }[st] || st;
  switch (h.hop) {
    case "wifi": {
      const signal = signalTone(v.signal_dbm ?? v.ap_signal_dbm);
      const w = st === "good" && signal !== "none" ? signal[0].toUpperCase() + signal.slice(1)
        : st === "poor" ? (signal === "weak" ? "Weak" : "Slow") : word;
      const fig = [v.signal_dbm ? `${v.signal_dbm} dBm` : "", v.link_mbps ? `${Math.round(v.link_mbps)} Mbit/s` : ""].filter(Boolean).join(" · ");
      return [`Wi-Fi${h.band ? `, ${h.band}` : ""}`, w, fig];
    }
    case "cable":
      return ["Cable", st === "unknown" ? "Not timed" : st === "good" ? "Good" : "Slow", v.router_ms ? ms(v.router_ms) : ""];
    case "backhaul":
      return ["To the router", st === "unknown" ? "Not timed" : st === "good" ? "Good" : "Slow", v.p50_ms ? ms(v.p50_ms) : ""];
    case "lines": {
      const n = v.lines || (h.lines || []).length;
      const busy = (st === "fair" || st === "poor") && (v.queue_ms || 0) > 50;
      const w = st === "unknown" ? "Not checked" : busy ? "Busy" : v.working === 0 && n ? "Down" : st === "good" ? "Good" : "Trouble";
      const fig = n ? `${v.working ?? n} of ${n} working${v.latency_ms ? `, ${ms(v.latency_ms)}` : ""}` : "";
      return [n === 1 ? "Internet line" : "Internet lines", w, fig];
    }
    case "internet":
      return ["Internet", st === "unknown" ? "Not timed yet" : st === "good" ? "Good" : "Slow",
        v.p50_ms === undefined ? "" : `${ms(v.p50_ms)} past ${cloud ? "Weavian's cloud" : "your line"}`];
    default:
      return [h.hop, word, ""];
  }
}

/** The stops on a device's route, between its hops. */
export function routeStops(d, hops, cloud) {
  const out = [{ icon: kindOf(d), name: "This device" }];
  hops.forEach((h, i) => {
    const next = hops[i + 1]?.hop;
    if (h.hop === "wifi") out.push(next === "backhaul" ? { icon: "ap", name: "Access point", sub: h.to } : { icon: "router", name: "Router" });
    else if (h.hop === "cable" || h.hop === "backhaul") out.push({ icon: "router", name: "Router" });
    else if (h.hop === "lines") out.push(!next ? { icon: "internet", name: "The internet" } : cloud ? { icon: "cloud", name: "Weavian cloud" } : { icon: "isp", name: "Your provider" });
    else if (h.hop === "internet") out.push({ icon: "internet", name: "The internet" });
  });
  return out;
}

/* Putting a device on the map by hand. */
export const PLACE_PICK = "Put a device on the map…";
export const placeHint = (name) => `Click the map where ${name} sits.`;
export const PLACE_OFF = "Take it off the map";

/* What would make the Wi-Fi better: each one sentence, worst first. */
const shareWord = (x) => (Math.abs(x - 1 / 3) < 0.04 ? "a third" : Math.abs(x - 0.25) < 0.03 ? "a quarter"
  : Math.abs(x - 0.5) < 0.04 ? "half" : Math.abs(x - 2 / 3) < 0.04 ? "two thirds" : pctWord(x));

export function extenderTip(e) {
  const after = e.weak_after > 0 ? `leave only ${pctWord(e.weak_after)} weak` : "cover them";
  return `${shareWord(e.weak_before)[0].toUpperCase()}${shareWord(e.weak_before).slice(1)} of the places walked have weak Wi-Fi. ` +
    `An extender at the star on the map should ${after}: the signal is still good there to pass on.`;
}

/** The channel a band would move to: the quietest its devices hear that
    needs no radar wait, as the box itself picks it (pkg/homewifi Pick). */
export function pickOf(r) {
  const p = r?.quietest && !r.quietest.radar ? r.quietest : r?.quietest_clear;
  return p && !p.radar ? p : null;
}

/* How much of a channel's air others take, as its devices hear it: "about 4
   in 10". */
const taken = (crowding) => (crowding >= 0.92 ? "nearly all" : `about ${Math.max(1, Math.round(crowding * 10))} in 10`);

/* What would make the Wi-Fi better, each a card: a title, one sentence, and
   what the box can do about it there and then. canTune says the box moves
   the access point's channel itself. */
export function betterItems(view, walk, { canTune = false } = {}) {
  const out = [];
  if (walk?.extender) {
    out.push({ tone: "todo", kind: "extender", key: "extender", title: "Add an extender", text: extenderTip(walk.extender), go: "hw-home", goLabel: "Show it on the map" });
  }
  const radios = view?.radios || [];
  const bandOf = (d) => radios.find((x) => x.name === d.station?.radio)?.band || d.client?.link?.band;
  const five = (view?.air?.radios || []).find((r) => r.band === "5 GHz");
  const good = [];
  for (const r of view?.air?.radios || []) {
    const sharing = r.sharing || [];
    const ap = radios.find((x) => x.band === r.band && x.device)?.device;
    const settings = ap || r.ap ? `the ${ap || r.ap}'s settings` : "this box's Wi-Fi settings";
    if (r.crowding >= 0.5 && sharing.length) {
      const loud = [...sharing].sort((a, b) => b.loudest_dbm - a.loudest_dbm)[0];
      const beside = loud.loudest_dbm >= -30 ? ` The loudest, ${loud.networks?.[0] || "a neighbour's"}, is right beside one of your devices.` : "";
      const crowd = `${sharing.length} nearby access ${sharing.length === 1 ? "point crowds" : "points crowd"} channel ${r.channel}`;
      const p = pickOf(r);
      if (p) {
        const tune = canTune && ap;
        out.push({
          tone: "todo", kind: "channel", key: `air-${r.band}`, band: r.band,
          title: `Move ${r.band} to channel ${p.channel}`,
          text: `${crowd}: others take ${taken(r.crowding)} of its air. On channel ${p.channel} they would take ${taken(p.crowding)}.${beside}${tune ? "" : ` Switch it in ${settings}.`}`,
          action: tune ? { label: `Switch to channel ${p.channel}`, band: r.band, to: p.channel } : null,
        });
      } else {
        out.push({ tone: "todo", kind: "channel", key: `air-${r.band}`, title: `Your ${r.band} Wi-Fi is crowded`, text: `${crowd}, and no channel without a radar wait is quieter.${beside}` });
      }
      const movable = r.band === "2.4 GHz" && five && five.crowding < r.crowding
        ? (view.devices || []).filter((d) => bandOf(d) === "2.4 GHz" && hears5(d)) : [];
      if (movable.length) {
        const list = listOf(movable.map(displayName));
        out.push({
          tone: "todo", kind: "band", key: "band", title: `Move ${list} to 5 GHz`,
          text: `Turn Wi-Fi off and on once on ${list}: ${movable.length === 1 ? "it is" : "they are"} on the crowded 2.4 GHz band and can use 5 GHz, which is far quieter here.`,
        });
      }
    } else if (r.crowding < 0.15) {
      good.push({ tone: "ok", kind: "ok", key: `air-${r.band}`, title: `${r.band} has channel ${r.channel} nearly to itself`, text: `Already good: your ${r.band} Wi-Fi has channel ${r.channel} nearly to itself.` });
    }
  }
  return [...out, ...good];
}

/** What came of a channel move, in a sentence. */
export function tuneSentence(t) {
  const at = new Date(t.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  // A person's change names everything it set; the box's own, the channel.
  const to = t.person
    ? `${t.to ? `channel ${t.to}` : "a channel it picks"}${t.width_mhz ? ` at ${t.width_mhz} MHz` : ""}, ${t.power_dbm} dBm`
    : `channel ${t.to}`;
  switch (t.state) {
    case "applying":
      return `Switching ${t.band} to ${to}. Devices on it join again within seconds.`;
    case "applied":
    case "kept":
    case "undoing":
    case "undone":
    case "worse":
      return `Switched ${t.band}${t.from ? ` from channel ${t.from}` : ""} to ${to} at ${at}.`;
    case "put_back":
      return `At ${at} the access point changed more than it was asked to, so the box put its settings back.`;
    default:
      return `Could not switch ${t.band} at ${at}: ${t.why || "the access point did not answer"}.`;
  }
}

export function confirmTune(action) {
  return {
    title: `Switch ${action.band} to channel ${action.to}?`,
    message: `The access point moves its ${action.band} Wi-Fi to channel ${action.to}. Every device on ${action.band} loses Wi-Fi for a few seconds while it moves, and joins again on its own (7 seconds in a test on 4 Nov). The box reads the access point back afterwards, and puts its settings back if anything else changed.`,
    confirmLabel: `Switch to channel ${action.to}`,
    variant: "primary",
  };
}

/* Channels and bands (HomeWifiChannels.js): the air of each band, its
   access point settings and its moves (homewifi air.go, tune.go,
   apsettings.go). */
export const CHANNELS_TITLE = "Channels and bands";
export const CHANNELS_HOW =
  "Each band as your devices hear it: every network around, and how crowded each channel is. Changing a band drops its devices " +
  "for a few seconds; they join again on their own. Channels that need a radar wait, which silence a band for a minute, are never used.";

export const airNow = (r) => `Now on channel ${r.channel}, ${r.width_mhz} MHz wide.`;

/** A band's air in a sentence: who shares its channel, and where it would be quieter. */
export function bandLine(r) {
  const n = (r.sharing || []).length;
  const share = r.crowding < 0.05 ? "taking almost none of its air" : `taking ${taken(r.crowding)} of its air`;
  const here = n
    ? `${n} other ${n === 1 ? "network shares" : "networks share"} channel ${r.channel}, ${share}.`
    : `Nobody else is heard on channel ${r.channel}: its air is your Wi-Fi's alone.`;
  const p = pickOf(r);
  if (p) return `${here} On channel ${p.channel} others would take ${taken(p.crowding)}.`;
  if (r.quietest?.radar) return `${here} The only quieter channels need a radar wait, which the box never uses.`;
  return n ? `${here} No other channel your devices hear is clearly quieter.` : here;
}

export const SPECTRUM_YOURS = "Your Wi-Fi";
export const SPECTRUM_RADAR = "Radar wait";
export const spectrumPick = (p) => `Quieter: ${p.channel}`;
export const shortName = (n) => (n.length > 16 ? `${n.slice(0, 15)}…` : n);

/** How a channel's air reads at a glance, by the share others take of it: a word and its tone. */
export function airMood(crowding) {
  if (crowding < 0.3) return { word: "Quiet", tone: "quiet" };
  if (crowding < 0.6) return { word: "Busy", tone: "busy" };
  return { word: "Crowded", tone: "crowded" };
}

export const CHANGE_BAND = "Change its channel, width or power";

/** What a wave in the spectrum is, for its hover. */
export const waveTitle = (s) =>
  `${(s.networks || []).join(", ") || "A hidden network"}: channel ${s.channel}${s.width_mhz ? `, ${s.width_mhz} MHz wide` : ""}, ${s.loudest_dbm} dBm where your devices are`;

export function spectrumKey(r) {
  const radar = r.band === "5 GHz" ? " Tinted: needs a radar wait." : "";
  return `Each wave is a network your devices hear, as wide as its channel and as tall as it is loud. Point at one for its name.${radar}`;
}

/* How much more of the air a move gave its devices, from the share others
   took before and after. */
function gainWords(before, after) {
  const ratio = (1 - after) / Math.max(1 - before, 0.01);
  if (ratio >= 1.9) return `its devices got about ${ratio < 2.5 ? "twice" : `${Math.round(ratio)} times`} the air`;
  return `its devices got about ${Math.round((ratio - 1) * 100)}% more of the air`;
}

/** What judging a move found, in a sentence; empty before it is judged. */
export function moveVerdict(t, devices = []) {
  if (t.state === "applied") return t.before ? "The box checks how its devices do five minutes after the move." : "";
  if (t.state === "undoing") return "It left the band worse, so the box is moving it back.";
  const b = t.before;
  const a = t.after;
  if (!t.verdict || !a || !b) return "";
  const named = (mac) => {
    const d = devices.find((x) => x.mac === mac);
    return d ? displayName(d) : "a device";
  };
  const why = {
    lost: `${listOf((t.lost || []).map(named))} did not come back to ${t.band}`,
    busier: `its air got busier: others took ${taken(a.crowding)} of it, against ${taken(b.crowding)} before`,
    slower: `its devices' links ran slower: ${Math.round(a.link_mbps)} Mb/s against ${Math.round(b.link_mbps)} before`,
    longer: `trips inside the home took longer: ${Math.round(a.inside_ms)} ms against ${Math.round(b.inside_ms)} before`,
  }[t.verdict];
  switch (t.state) {
    case "kept":
      return t.verdict === "better" ? `Kept: ${gainWords(b.crowding, a.crowding)}, and every device came back.` : "Kept: no worse for its devices.";
    case "undone":
      return `Moved back: ${why}. The box will not pick channel ${t.to} again today.`;
    case "worse":
      return t.person ? `Left as you set it, though ${why}.` : `Left there, though ${why}: moving back would risk a radar wait.`;
    default:
      return "";
  }
}

/** Names in a list: "a", "a and b", "a, b and c". */
export function listOf(names) {
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "";
}

export function apNow(r) {
  const ch = r.channel ? `channel ${r.channel}` : "a channel of its own pick";
  const wide = r.width_mhz ? `${r.width_mhz} MHz wide` : "a width of its own pick";
  return `Now on ${ch}, ${wide}, sending at ${r.power_dbm} dBm.`;
}

/** Said under a band already on a channel that needs a radar wait, which a person chose on the access point itself. */
export const AP_ON_RADAR =
  "This channel needs a radar wait. It was set on the access point itself; the box keeps it, and moves the band only to a channel without one.";

export function channelLabel(c) {
  return `${c.channel === 0 ? "Its own pick" : c.channel}${c.radar ? " (radar wait)" : ""}`;
}

export function widthLabel(w) {
  const name = w.mhz ? `${w.mhz} MHz` : "Its own pick";
  return w.channels.some((c) => !c.radar) ? name : `${name} (radar wait)`;
}

/** The powers offered: the most the channel allows, then a half, a quarter, an eighth and a sixteenth of it. */
export function powerChoices(max, current) {
  const out = [0, 3, 6, 9, 12].map((d) => max - d).filter((p) => p >= 1);
  if (current <= max && !out.includes(current)) out.push(current);
  return out.sort((a, b) => b - a);
}

export const powerLabel = (p, max) => (p === max ? `${p} dBm, the most allowed` : `${p} dBm`);

export function apRadar(d, r) {
  const where = d.channel ? `Channel ${d.channel}` : "Its own pick";
  return `${where} at ${d.width ? `${d.width} MHz` : "its own width"} needs a radar wait: nothing on ${r.band} would get through for a minute. Pick another.`;
}

export const apNotOffered = (d, r) => `${r.band} offers no channel ${d.channel} at that width. Pick another.`;

export function confirmAP(r, d) {
  const to = `${d.channel ? `channel ${d.channel}` : "a channel it picks"}${d.width ? ` at ${d.width} MHz` : ""}, ${d.power} dBm`;
  return {
    title: `Change the ${r.band} Wi-Fi?`,
    message: `The access point sets its ${r.band} Wi-Fi to ${to}. Every device on ${r.band} loses Wi-Fi for a few seconds while it changes, and joins again on its own. The box reads the access point back afterwards, and puts its settings back if anything else changed.`,
    confirmLabel: "Change it",
    variant: "primary",
  };
}

export const STEER_SHORT = "Band steering moves devices that can use 5 GHz onto it, where they get more of the air.";
export const STEER_MODES = [
  { value: "prefer_5", label: "Prefer 5 GHz" },
  { value: "balance", label: "Balance the bands" },
  { value: "off", label: "Off" },
];

/* The tuning setting, as a person chooses it. */
export const TUNE_MODES = [
  { value: "ask", label: "Only when I ask" },
  { value: "auto", label: "By itself" },
  { value: "off", label: "Off" },
];

export const TUNE_SHORT =
  "Pick the quietest channel: by itself, the box moves a band to a channel clearly quieter for 15 minutes, " +
  "and only while no device on that band is in use, since its devices lose Wi-Fi for a few seconds as it moves. " +
  "Five minutes later it checks the band's devices do better, and moves back if they do not. " +
  "Never to a channel shared with radar, which would silence the band for a minute.";

export const ROOM_SHORT =
  "Make room for calls, games and video: when one struggles because another device runs a big download, the box " +
  "slows the download for a while, checks it helped, and gives the speed back if it did not.";

/** The thing that sends the home's Wi-Fi, as the map and the sentences name it. */
export function wifiSource(radios = []) {
  const ap = radios.find((r) => r.device)?.device;
  return ap ? { name: "Access point", model: ap, the: `the access point (${ap})` } : { name: "Router", model: "", the: "the router" };
}
